import React from 'react';

import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { useAppStore } from 'src/hooks/useAppStore';
import { CountryCode } from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { ONE_TRUST_COOKIE_CONTRIES } from 'src/utils/oneTrustHelper';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import * as style from './FooterMenuStyle';
import { mixpanelObject } from '../../utils/analytics';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_ELEMENT,
  MIXPANEL_FOOTER
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  ctaEvent,
  timeEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { clearCtaEventsProperty } from '../../utils/analytics/mixpanel/mixpanelHelper';
import { globalUrlHelper } from '../../utils/common';
import { DynamicObject } from '../../utils/commonTypes';
import { FOOTER_MENU } from '../../utils/constants/FooterConstants';

type FooterMenuProps = {
  footerStrings: DynamicObject;
};

const FooterMenu = ({ footerStrings }: FooterMenuProps) => {
  const router = useRouter();

  const { usersLocationData, isUserLoggedIn, remoteConfig } = useAppStore();
  const country = usersLocationData?.country || 'IN';
  const countryCode = usersLocationData?.country_code || CountryCode.IN;
  const Container = styled.div`
    ${style.footerMenu};
  `;

  const consentSettings = () => {
    if (window && window.OneTrust) window.OneTrust.ToggleInfoDisplay();
  };
  const showBlogLinkIndia =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.FEATURE_BLOG_LINK) ||
    false;

  const showBlogLinkGlobal =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_BLOG_LINK_GLOBAL
    ) || false;

  let showBlogLink = false;

  if (
    (countryCode === 'IN' && showBlogLinkIndia) ||
    (countryCode !== 'IN' && showBlogLinkGlobal)
  ) {
    showBlogLink = true;
  } else {
    showBlogLink = false;
  }

  const trackMixpanelEvents = (element: string) => {
    clearCtaEventsProperty(mixpanelObject);
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, element);
    mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_FOOTER);
    ctaEvent();
  };

  const navigate = (
    link: string,
    element: string,
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (event) {
      event.preventDefault();
    }
    if (link !== location.pathname && link) {
      timeEvent();
      const displayLanguage = router.locale || 'en';
      // mixpanel Initailization start
      trackMixpanelEvents(element);
      // mixpanel Initailization end
      if (link === '/help') {
        // Global FAQ to open page within App
        const url = `${
          process.env.PUBLIC_URL
        }/help/?translation=${displayLanguage}&country=${country}&platform=desktop_web&user_type=${
          isUserLoggedIn ? 'loggedin' : 'guest'
        }&app_version=${process.env.APP_VERSION}`;
        window?.open(url, '_blank');
      } else if (
        link === '/aboutus' ||
        link === '/privacypolicy' ||
        link === '/termsofuse'
      ) {
        router.push(link);
      } else {
        window.location.href = `${link}`;
      }
    }
  };
  return (
    <>
      <Container data-test-id="container">
        <ul>
          <li>
            <a
              href={globalUrlHelper('/tv-shows/tv-channels')}
              className="noSelect"
              onClick={(e) => {
                navigate('/tv-shows/tv-channels', 'tvshows', e);
              }}>
              {FOOTER_MENU.CHANNELS}
            </a>
          </li>
          <li>
            <a
              href={globalUrlHelper('/aboutus')}
              className="noSelect"
              data-testid="about-us-link"
              onClick={(e) => {
                navigate('/aboutus', FOOTER_MENU.ABOUT_US, e);
              }}>
              {footerStrings.ABOUTUS}
            </a>
          </li>
          <li>
            {country === 'IN' ? (
              <a
                href="https://helpcenter.zee5.com/portal/en/home"
                className="noSelect"
                onClick={(e) => {
                  navigate('/help', FOOTER_MENU.HELP_CENTER, e);
                }}>
                {footerStrings.FAQ}
              </a>
            ) : (
              <a
                href="https://helpcenter.zee5.com/portal/en/home"
                className="noSelect globalDiv"
                onClick={(e) => {
                  navigate('/help', FOOTER_MENU.HELP_CENTER, e);
                }}>
                {footerStrings.FAQ}
              </a>
            )}
          </li>
          <li>
            <a
              href={globalUrlHelper('/privacypolicy')}
              className="noSelect"
              onClick={(e) => {
                navigate('/privacypolicy', FOOTER_MENU.PRIVACY_POLICY, e);
              }}>
              {footerStrings.PRIVACY_POLICY}
            </a>
          </li>
          <li>
            <a
              href={globalUrlHelper('/termsofuse')}
              className="noSelect"
              onClick={(e) => {
                navigate('/termsofuse', FOOTER_MENU.TERMS_OF_USE, e);
              }}>
              {footerStrings.TERMS}
            </a>
          </li>

          <li>
            <a
              href={globalUrlHelper('/consent-manager')}
              className="noSelect"
              onClick={(e) => {
                navigate('/consent-manager', FOOTER_MENU.PREFERENCES, e);
              }}>
              {footerStrings.PREFERENCES}
            </a>
          </li>

          <li>
            <a
              href={globalUrlHelper('/consent-manager')}
              className="noSelect"
              onClick={(e) =>
                navigate(
                  '/consent-manager',
                  FOOTER_MENU.DO_NOT_SELL_SHARE_PI,
                  e
                )
              }>
              {footerStrings.DO_NOT_SELL_SHARE_PI}
            </a>
          </li>

          {ONE_TRUST_COOKIE_CONTRIES.includes(countryCode) ? (
            <li>
              <div className="consentSettingsStyle" onClick={consentSettings}>
                {footerStrings.CONSENT_SETTINGS}
              </div>
            </li>
          ) : null}
          {showBlogLink ? (
            <li>
              <a
                href={globalUrlHelper('/blog/')}
                className="noSelect"
                onClick={(e) => {
                  navigate('/blog/', FOOTER_MENU.BLOG, e);
                }}>
                {footerStrings.BLOG}
              </a>
            </li>
          ) : null}
        </ul>
      </Container>
    </>
  );
};

export default FooterMenu;
